import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getProfileApi, putProfileApi } from '~/api/profile';
import type { Member } from '~/types/auth';

interface GetProfileOptions {
  force?: boolean;
  revalidate?: boolean;
}

const PROFILE_TTL_MS = 10 * 60 * 1000;

export const useProfileStore = defineStore('profile', () => {
  const profile = ref<Member | null>(null);
  const lastFetchedAt = ref<number | null>(null);
  const isRefreshing = ref(false);

  const fetchProfile = async () => {
    const response = await getProfileApi();
    setProfile(response);
    lastFetchedAt.value = Date.now();
  };

  const backgroundRevalidate = async () => {
    if (isRefreshing.value) {
      return;
    }
    isRefreshing.value = true;
    try {
      await fetchProfile();
    } catch (error) {
      console.error('Background revalidate profile failed:', error);
    } finally {
      isRefreshing.value = false;
    }
  };

  const getProfile = async (options?: GetProfileOptions) => {
    const force = options?.force === true;
    const now = Date.now();

    if (!force && profile.value && lastFetchedAt.value) {
      const isFresh = now - lastFetchedAt.value <= PROFILE_TTL_MS;

      if (isFresh) {
        if (options?.revalidate === true) {
          void backgroundRevalidate();
        }
        return profile.value;
      }

      void backgroundRevalidate();
      return profile.value;
    }

    await fetchProfile();
    return profile.value;
  };

  const updateProfileNickname = async (nickname: string) => {
    const data = await getProfile();
    if (data) {
      const newData = { ...data, nickname };
      const response = updateProfile(newData);
      return response;
    }
    return data;
  };

  function clearProfile() {
    profile.value = null;
    lastFetchedAt.value = null;
    isRefreshing.value = false;
  }

  async function updateProfile(data: Member) {
    const response = await putProfileApi(data);
    setProfile(response);
    lastFetchedAt.value = Date.now();
  }

  function setProfile(partialProfile: Partial<Member>) {
    profile.value = partialProfile as Member;
  }

  const reset = () => {
    clearProfile();
  };

  return {
    profile,
    getProfile,
    fetchProfile,
    clearProfile,
    updateProfile,
    updateProfileNickname,
    reset,
  };
});
